import { useQuery } from '@tanstack/react-query';
import type { InsightsBrandFunnelTimeSeriesBucket } from '@brand24-original/llm-types';
import { GET_INSIGHTS_BRAND_FUNNELS_TIMESERIES } from '../../query/insights/keys';
import { getInsightsBrandFunnelsTimeSeries } from '../../services/insights/InsightsStatsService';
import { ChatType } from '../../../types/monitor/ChatType';
import { PeriodType } from '../../../types/prompt/PeriodType';

export interface InsightsBrandFunnelsTimeSeriesParams {
  projectId: number;
  chatType: ChatType;
  period: PeriodType;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterChatTypes?: string[];
  /**
   * The analyzed brand (project.firmName). Key-only: the backend reads it from the project, this just
   * forces a refetch when the ChangeKeywordBox switches it.
   */
  brand?: string;
}

export const useGetInsightsBrandFunnelsTimeSeries = (
  params: InsightsBrandFunnelsTimeSeriesParams | null
) => {
  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<InsightsBrandFunnelTimeSeriesBucket[]>({
    queryKey: [
      GET_INSIGHTS_BRAND_FUNNELS_TIMESERIES,
      params?.projectId,
      params?.chatType,
      params?.period,
      fromTimestamp,
      toTimestamp,
      params?.monitorPromptIds,
      params?.filterChatTypes,
      params?.brand,
    ],
    // `enabled` gates on params/from/to, so the assertions below cannot fire at runtime.
    queryFn: () =>
      getInsightsBrandFunnelsTimeSeries(
        params!.projectId,
        params!.chatType,
        params!.period,
        params!.from!,
        params!.to!,
        params!.monitorPromptIds,
        params!.filterChatTypes
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled:
      !!params &&
      !!params.from &&
      !!params.to &&
      !!params.chatType &&
      !!params.period &&
      !!params.projectId,
  });

  return {
    insightsBrandFunnelsTimeSeries: query.data ?? null,
    // isPending, not isLoading — see the comment in useGetInsightsOverview.
    isLoading: query.isPending,
    isError: query.isError,
  };
};
